import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { User } from "firebase/auth";
import {
  requestEmailVerification,
  subscribeToAuthChanges,
} from "@/firebase/auth-modular";
import { refreshCurrentAuthUser } from "@/firebase/auth-state";
import { isRestrictedForUnverifiedEmail } from "@/utils/auth/emailVerification";
import "./UnverifiedEmailBanner.css";

/**
 * Routes that already explain the confirmation themselves. Repeating the nudge
 * above /verify-email or the login form is noise, not pressure.
 */
const SUPPRESSED_PATH_PREFIXES = [
  "/login",
  "/signup",
  "/verify-email",
  "/auth/action",
  "/admin/login",
];

const DISMISS_KEY = "wn.unverifiedEmailBanner.dismissed";

/** sessionStorage throws in some privacy modes; a missing dismissal is fine. */
function readDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

function persistDismissed(): void {
  try {
    window.sessionStorage.setItem(DISMISS_KEY, "1");
  } catch {
    /* dismissal just doesn't survive this navigation */
  }
}

type SendState =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "sent"; message: string }
  | { kind: "failed"; error: string };

/**
 * Standing reminder for accounts that skipped confirmation.
 *
 * `/verify-email` lets a new signup "skip for now and look around", and every
 * account created before confirmation shipped is unverified already. Those
 * members otherwise meet the restriction for the first time as a 403 at
 * checkout, so the nudge lives in the shell where they are actually browsing.
 *
 * In-app rather than another email on purpose: these are signed-in people, and
 * the sending domain is new enough that mail to a stale list would cost more
 * reputation than it recovers.
 */
const UnverifiedEmailBanner: React.FC = () => {
  const location = useLocation();
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [send, setSend] = useState<SendState>({ kind: "idle" });
  const [checking, setChecking] = useState(false);
  const [checkedUnverified, setCheckedUnverified] = useState(false);

  useEffect(() => subscribeToAuthChanges(setUser), []);

  const dismiss = useCallback(() => {
    persistDismissed();
    setDismissed(true);
  }, []);

  const resend = useCallback(async () => {
    setSend({ kind: "sending" });
    setCheckedUnverified(false);
    const result = await requestEmailVerification(user ?? null);
    setSend(
      result.success
        ? { kind: "sent", message: result.message || "Verification email sent." }
        : {
            kind: "failed",
            error: result.error || "Failed to send verification email.",
          },
    );
  }, [user]);

  /**
   * The cached session keeps reporting `emailVerified: false` after the link is
   * opened in another tab, so re-read from Firebase before believing it.
   */
  const recheck = useCallback(async () => {
    setChecking(true);
    setCheckedUnverified(false);
    setSend({ kind: "idle" });
    try {
      const refreshed = await refreshCurrentAuthUser();
      if (refreshed?.emailVerified) {
        setUser(refreshed);
        return;
      }
      setCheckedUnverified(true);
    } finally {
      setChecking(false);
    }
  }, []);

  const suppressedHere = SUPPRESSED_PATH_PREFIXES.some((prefix) =>
    location.pathname.startsWith(prefix),
  );

  if (dismissed || suppressedHere || !isRestrictedForUnverifiedEmail(user)) {
    return null;
  }

  return (
    <div className="unverified-email-banner" role="region" aria-label="Email confirmation">
      <div className="unverified-email-banner__inner">
        <p className="unverified-email-banner__copy">
          Booking, hosting and membership stay locked until you confirm{" "}
          <strong className="unverified-email-banner__email">
            {user?.email}
          </strong>
          . The link may have landed in your spam folder.
        </p>

        <div className="unverified-email-banner__actions">
          <button
            type="button"
            className="unverified-email-banner__btn"
            onClick={() => void recheck()}
            disabled={checking}
          >
            {checking ? "Checking…" : "I confirmed it"}
          </button>
          <button
            type="button"
            className="unverified-email-banner__btn"
            onClick={() => void resend()}
            disabled={send.kind === "sending"}
          >
            {send.kind === "sending" ? "Sending…" : "Send a new link"}
          </button>
          <button
            type="button"
            className="unverified-email-banner__dismiss"
            onClick={dismiss}
            aria-label="Hide until next visit"
            title="Hide until next visit"
          >
            ×
          </button>
        </div>

        {send.kind === "sent" ? (
          <p className="unverified-email-banner__status" role="status">
            {send.message}
          </p>
        ) : null}
        {send.kind === "failed" ? (
          <p
            className="unverified-email-banner__status unverified-email-banner__status--error"
            role="alert"
          >
            {send.error}
          </p>
        ) : null}
        {checkedUnverified ? (
          <p className="unverified-email-banner__status" role="status">
            Still not confirmed. Open the link in the email, then check again.
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default UnverifiedEmailBanner;
